import axios from 'axios'
import { LogLeadTemp } from '../interfaces/LogLeadTemp'
import { convertToFormDataDynamic, chunkDataBySize, calculateChunkSize, formatFileSize } from '../functions/formDataUtils'

const API_BASE_URL = process.env.REACT_APP_API_URL

export interface BulkUploadProgress {
  totalChunks: number
  currentChunk: number
  totalItems: number
  processedItems: number
  currentChunkSize: string
  totalSize: string
  percentage: number
  isCompleted: boolean
  errors: string[]
}

export interface BulkUploadResult {
  success: boolean
  totalProcessed: number
  totalErrors: number
  errors: string[]
  results: any[]
  dataSuccess: { id: string, leadId: string }[]
  dataError: any[]
}

/**
 * Upload a single chunk of LogLeadTemp data
 */
const uploadChunk = async (
  templatePipelineId: string,
  chunk: LogLeadTemp[],
  token: string,
  onProgress?: (progress: any) => void
): Promise<any> => {
  try {
    const formData = convertToFormDataDynamic(chunk)

    // for (let [key, value] of formData.entries()) {
    //   if (value instanceof File) {
    //     console.log(key, '→', `File { name: "${value.name}", size: ${value.size}, type: "${value.type}" }`)
    //   } else {
    //     console.log(key, '→', value)
    //   }
    // }

    // Mock response for testing
    // return {
    //   success: true,
    //   processed: chunk.length,
    //   message: `Successfully processed ${chunk.length} items`
    // }

    // Uncomment when API is ready:
    const response = await axios.post(
      `${API_BASE_URL}lead_logs/insert-bulk`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Auth-Token': token
        },
        onUploadProgress: onProgress,
        timeout: 300000, // 5 minutes timeout
      }
    )
    return response.data
  } catch (error) {
    console.error('Error uploading chunk:', error)
    throw error
  }
}

/**
 * Bulk upload LogLeadTemp data with chunking and progress tracking
 */
export const bulkUploadLogLeadTemp = async (
  templatePipelineId: string,
  data: LogLeadTemp[],
  token: string,
  onProgress?: (progress: BulkUploadProgress) => void,
  maxChunkSizeBytes: number = 20 * 1024 * 1024 // 20MB default
): Promise<BulkUploadResult> => {
  try {
    // Split data into chunks based on file size
    const chunks = chunkDataBySize(data, maxChunkSizeBytes)
    const totalSize = calculateChunkSize(data)

    const result: BulkUploadResult = {
      success: true,
      totalProcessed: 0,
      totalErrors: 0,
      errors: [],
      results: [],
      dataSuccess: [],
      dataError: []
    }

    let processedItems = 0

    // Process each chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i]
      const chunkSize = calculateChunkSize(chunk)

      try {
        // Update progress
        if (onProgress) {
          onProgress({
            totalChunks: chunks.length,
            currentChunk: i + 1,
            totalItems: data.length,
            processedItems: processedItems,
            currentChunkSize: formatFileSize(chunkSize),
            totalSize: formatFileSize(totalSize),
            percentage: Math.round((processedItems / data.length) * 100),
            isCompleted: false,
            errors: result.errors
          })
        }

        console.log(`Uploading chunk ${i + 1}/${chunks.length} (${chunk.length} items, ${formatFileSize(chunkSize)})`)

        // Upload chunk with individual progress tracking
        const chunkResult = await uploadChunk(
          templatePipelineId,
          chunk,
          token,
          (progressEvent) => {
            // Individual chunk upload progress can be handled here if needed
            // console.log(`Chunk ${i + 1} upload progress: ${Math.round((progressEvent.loaded / (progressEvent.total || 1)) * 100)}%`)
          }
        )

        result.results.push(chunkResult)
        result.dataSuccess.push(...chunkResult.data.success)
        result.dataError.push(...chunkResult.data.error)
        result.totalProcessed += chunk.length
        processedItems += chunk.length

        console.log(`Chunk ${i + 1} uploaded successfully`)

      } catch (error) {
        const errorMessage = `Failed to upload chunk ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`
        console.error(errorMessage)

        result.errors.push(errorMessage)
        result.totalErrors += chunk.length
        result.success = false

        // Continue with other chunks even if one fails
      }
    }

    // Final progress update
    if (onProgress) {
      onProgress({
        totalChunks: chunks.length,
        currentChunk: chunks.length,
        totalItems: data.length,
        processedItems: result.totalProcessed,
        currentChunkSize: '',
        totalSize: formatFileSize(totalSize),
        percentage: 100,
        isCompleted: true,
        errors: result.errors
      })
    }

    console.log(`Bulk upload completed. Processed: ${result.totalProcessed}, Errors: ${result.totalErrors}`)

    return result

  } catch (error) {
    console.error('Error in bulk upload:', error)
    throw new Error(`Bulk upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Validate data before upload (check for required fields, file sizes, etc.)
 */
export const validateDataForUpload = (data: LogLeadTemp[]): { isValid: boolean, errors: string[] } => {
  const errors: string[] = []

  if (!data || data.length === 0) {
    errors.push('No data to upload')
    return { isValid: false, errors }
  }

  data.forEach((item, index) => {
    // Check required fields
    if (!item.leadId) {
      errors.push(`Item ${index + 1}: Missing leadId`)
    }

    // Check file sizes in attachments
    if (item.attachments && Array.isArray(item.attachments)) {
      item.attachments.forEach((file, fileIndex) => {
        if (file instanceof File && file.size > 50 * 1024 * 1024) { // 50MB limit per file
          errors.push(`Item ${index + 1}, attachment ${fileIndex + 1}: File too large (${formatFileSize(file.size)}, max 50MB)`)
        }
      })
    }

    // Check custom field files
    if (item.customFields) {
      Object.keys(item.customFields).forEach(key => {
        const value = item.customFields[key]
        if (value instanceof File && value.size > 50 * 1024 * 1024) {
          errors.push(`Item ${index + 1}, custom field ${key}: File too large (${formatFileSize(value.size)}, max 50MB)`)
        }
      })
    }
  })

  return {
    isValid: errors.length === 0,
    errors
  }
} 